// src/utils/calcBookingBreakdown.ts
import { HST_RATE, MAT_RATE } from "constants/constant";
import moment from "moment";
import { round2 } from "utils/calc";

type TripLike = any;


const formatMoney = (v: number) => round2(v).toFixed(2);

const getCancellationFeeTotal = (trip: any) => {
  const bills = trip?.bills || [];

  return round2(
    bills.reduce((billSum: number, bill: any) => {
      const charges = bill?.charges || [];

      const cancellationFeeInBill = charges.reduce((chargeSum: number, charge: any) => {
        if (charge?.isDeleted) return chargeSum;

        const type = String(charge?.type || "").toUpperCase();
        const description = String(charge?.description || "");

        const isCancellationFee =
          type === "EXTRA" && description.toLowerCase().startsWith("cancellation fee");

        if (!isCancellationFee) return chargeSum;

        return chargeSum + Number(charge?.amount || 0);
      }, 0);

      return billSum + cancellationFeeInBill;
    }, 0)
  );
};

const getExtraChargeLines = (trip: any): BreakdownLine[] => {
  const bills = trip?.bills || [];
  const lines: BreakdownLine[] = [];

  for (const bill of bills) {
    for (const charge of bill?.charges || []) {
      if (charge?.isDeleted) continue;

      const type = String(charge?.type || "").toUpperCase();
      const description = String(charge?.description || "").trim();
      const amount = Number(charge?.amount || 0);

      if (type !== "EXTRA") continue;
      if (amount <= 0) continue;

      // Already handled separately
      if (description.toLowerCase().startsWith("cancellation fee")) continue;

      lines.push({
        key: `extra_${charge?.id || lines.length}`,
        title: description || "Extra charge",
        amount: round2(amount),
      });
    }
  }

  return lines;
};

const guessUnitLabel = (title: string, isOneTime: boolean, qty: number) => {
  const t = title.toLowerCase();

  if (t.includes("breakfast")) return (qty<=1 ? "person" : "people");
  if (t.includes("parking")) return (qty <= 1 ? "car" : "cars");

  if (isOneTime) return (qty <= 1 ? "room" : "rooms");

  return (qty <= 1 ? "unit" : "units");
};

const getBookingRoomTitle = (booking: any) => {
  const rb0 =
    pickRoomBookingsToCount(booking)?.[0] || booking?.roomBookings?.[0];
  const roomType = rb0?.room?.roomType;

  const name = roomType?.name || "Room";
  const bed = roomType?.bed ? ` - ${roomType.bed}` : "";
  const windows = roomType?.hasWindows ? "w/ window" : "w/ skylight";

  const checkIn = booking?.checkInDate
    ? moment(booking.checkInDate).format("ddd, DD MMM")
    : "";

  const checkOut = booking?.checkOutDate
    ? moment(booking.checkOutDate).format("ddd, DD MMM")
    : "";

  const range =
    checkIn && checkOut ? `${checkIn} - ${checkOut}` : checkIn || checkOut;

  return `${name}${bed} ${windows}${range ? ` (${range})` : ""}`;
};

const pickRoomBookingsToCount = (booking: any) => {
  const notDeleted = (booking?.roomBookings || []).filter(
    (rb: any) => !rb?.isDeleted
  );

  const current = notDeleted.filter((rb: any) => rb?.isCurrent);

  // main path
  if (current.length > 0) return current;

  // simple safe fallback: count ONE room booking only
  return notDeleted.length > 0 ? [notDeleted[notDeleted.length - 1]] : [];
};

export type BreakdownLine = {
  key: string;
  title: string;
  amount: number; // can be negative for discounts
  red?: boolean; // use red style in UI
};

export type BookingBreakdown = {
  lines: BreakdownLine[];
  mat: number;
  hst: number;
  total: number;
};

export const calcBookingBreakdown = (trip: TripLike): BookingBreakdown => {

  // 1) Rooms base (ONE line per booking)
  let roomsBase = 0;
  const roomLines: BreakdownLine[] = [];

  for (const booking of trip?.bookings || []) {
    
    if (booking?.isDeleted) continue;

    let bookingRoomsTotal = 0;

    const roomBookingsToCount = pickRoomBookingsToCount(booking);

    for (const rb of roomBookingsToCount) {
      const totalPrice = Number(rb?.totalPrice || 0);
      const pricePerNight = Number(rb?.pricePerNight || 0);
      const nights = Number(rb?.numberOfNights || 0);

      bookingRoomsTotal += totalPrice > 0 ? totalPrice : pricePerNight * nights;
    }

    bookingRoomsTotal = round2(bookingRoomsTotal);

     // Do not show room lines that no longer contribute to the bill
    if (bookingRoomsTotal > 0) {
      roomsBase += bookingRoomsTotal;
    
      roomLines.push({
        key: `booking_${booking?.bookingId || roomLines.length}`,
        title: getBookingRoomTitle(booking),
        amount: bookingRoomsTotal,
      });
    }
  }

  roomsBase = round2(roomsBase);

  // 2) Coupon discount (fixed or percent)
  const coupon = trip?.coupon;
  const couponDiscountRaw = Number(coupon?.discount || 0);
  const couponRateType = String(coupon?.rateType || "").toUpperCase();

  let couponDiscount = 0;
  if (couponDiscountRaw > 0) {
    couponDiscount = couponRateType.includes("FIXED")
      ? couponDiscountRaw
      : (roomsBase * couponDiscountRaw) / 100;

    // Discount can't exceed base amount
    couponDiscount = Math.min(couponDiscount, roomsBase);
  }

  couponDiscount = round2(couponDiscount);
  const afterCoupon = round2(roomsBase - couponDiscount);

  // 3) Rate plan discount (percent)
  const ratePlanPercent = Number(
    trip?.ratePlan?.ratePlan?.rateChangeValue || 0
  );
  const ratePlanDiscount =
    ratePlanPercent > 0 ? round2((afterCoupon * ratePlanPercent) / 100) : 0;

  const afterDiscounts = round2(afterCoupon - ratePlanDiscount);

  // 4) MAT
  const mat = round2(afterDiscounts * MAT_RATE);

  // 5) Add-ons total (split by add-on)
  let addonsTotal = 0;

  type AddonAgg = {
    key: string;
    title: string;
    unitPrice: number;
    isOneTime: boolean;

    qty: number; // total quantity across bookings
    unitNights: number; // only for per-night: sum(qty * bookingNights)
    amount: number; // total amount for this add-on
  };

  const addonMap = new Map<string, AddonAgg>();

  for (const booking of trip?.bookings || []) {
    // Pricing rules (current design):
    // - One-time add-ons: unitPrice * qty
    // - Per-night add-ons: unitPrice * qty * addon.numberOfNights (fallback to booking nights)
    // - addon.numberOfNights can differ from booking nights (breakfast/parking UI)

    if (booking?.isDeleted) continue;

    const roomBookingsToCount = pickRoomBookingsToCount(booking);

    const bookingNights = roomBookingsToCount.reduce(
      (sum: number, rb: any) => sum + Number(rb?.numberOfNights || 0),
      0
    );

    for (const addon of booking?.addons || []) {
      if (addon?.isDeleted) continue;
      if (addon?.bookingAddOn?.isDeleted) continue;

      const qty = Number(addon?.quantity || 0);
      if (qty <= 0) continue;

      const title =
        addon?.bookingAddOn?.title || addon?.bookingAddOn?.label || "Add-on";

      const unitPrice =
        Number(addon?.bookingAddOn?.pricePerNight || 0) > 0
          ? Number(addon?.bookingAddOn?.pricePerNight || 0)
          : Number(addon?.pricePerNight || 0);

      if (unitPrice <= 0) continue;

      const isOneTime = !!addon?.bookingAddOn?.isOneTimePriced;

      // NEW: use addon.numberOfNights if backend provided it, otherwise fallback to booking nights
      const addonNightsRaw = Number(addon?.numberOfNights);
      const addonNights =
        !isOneTime && addonNightsRaw > 0 ? addonNightsRaw : bookingNights;



      // Key: group by add-on id + pricing mode + unit price (safe if price differs)
      const idKey =
      addon?.bookingAddOn?.addOnId ||
      addon?.bookingAddOn?.id ||
      addon?.bookingAddOn?.title ||
      addon?.id ||
      title;
      const key = `${idKey}__${isOneTime ? "one" : "night"}__${unitPrice}__${
        isOneTime ? "na" : addonNights
      }`;

      const amount = isOneTime
        ? unitPrice * qty
        : unitPrice * qty * addonNights;

      addonsTotal += amount;

      const existing = addonMap.get(key);
      if (!existing) {
        addonMap.set(key, {
          key,
          title,
          unitPrice,
          isOneTime,
          qty,
          unitNights: isOneTime ? 0 : qty * addonNights,
          amount,
        });
      } else {
        existing.qty += qty;
        if (!isOneTime) existing.unitNights += qty * addonNights;
        existing.amount += amount;
      }
    }
  }

  addonsTotal = round2(addonsTotal);

  const cancellationFeeTotal = getCancellationFeeTotal(trip);
  
  const extraChargeLines = getExtraChargeLines(trip);
  const extraChargesTotal = round2(
    extraChargeLines.reduce((sum, line) => sum + line.amount, 0)
  );


  // Build add-on breakdown lines (one per add-on type)
  const addonLines: BreakdownLine[] = Array.from(addonMap.values())
    .sort((a, b) => a.title.localeCompare(b.title))
    .map((a) => {
      const unitLabel = guessUnitLabel(a.title, a.isOneTime, a.qty);

      if (a.isOneTime) {
        // Example: Late Checkout (2 rooms @ $25/room)
        return {
          key: `addon_${a.key}`,
          title: `${a.title} (${a.qty} ${unitLabel} @ $${formatMoney(
            a.unitPrice
          )}/room)`,
          amount: round2(a.amount),
        };
      }

      // Per-night example: Parking (2 nights, 2 cars @ $10/night)
      // If unitNights divisible by qty => we can show nights nicely.
      const canDeriveNights = a.qty > 0 && a.unitNights % a.qty === 0;
      const nights = canDeriveNights ? a.unitNights / a.qty : null;

      const title =
        nights !== null
          ? `${a.title} (${nights} ${nights === 1 ? "night" : "nights"}, ${
              a.qty
            } ${unitLabel} @ $${formatMoney(a.unitPrice)}/night)`
          : `${a.title} (${a.unitNights} unit-nights @ $${formatMoney(
              a.unitPrice
            )}/night)`;

      return {
        key: `addon_${a.key}`,
        title,
        amount: round2(a.amount),
      };
    });

  // 6) HST
  const beforeHst = round2(afterDiscounts + mat + addonsTotal + cancellationFeeTotal + extraChargesTotal);

  const hst = round2(beforeHst * HST_RATE);

  const total = round2(beforeHst + hst);

  // Minimal breakdown lines for UI
  const lines: BreakdownLine[] = [...roomLines];

  if (couponDiscount > 0) {
    lines.push({
      key: "coupon",
      title: coupon?.title ? `Coupon (${coupon.title})` : "Coupon",
      amount: -couponDiscount,
      red: true,
    });
  }

  if (ratePlanDiscount > 0) {
    lines.push({
      key: "ratePlan",
      title: `Advanced Purchase (${ratePlanPercent}% off)`,
      amount: -ratePlanDiscount,
      red: true,
    });
  }

  if (mat > 0) {
    lines.push({
      key: "mat",
      title: `Municipal Accommodation Tax (${Math.round(MAT_RATE * 100)}%)`,
      amount: mat,
    });
  }

  if (addonsTotal > 0) {
    lines.push(...addonLines);
  }

  if (extraChargeLines.length > 0) {
    lines.push(...extraChargeLines);
  }

  if (cancellationFeeTotal > 0) {
    lines.push({
      key: "cancellationFee",
      title: "Cancellation fee",
      amount: cancellationFeeTotal,
      red: true,
    });
  }

  if (hst > 0) {
    lines.push({
      key: "hst",
      title: `HST (${Math.round(HST_RATE * 100)}%)`,
      amount: hst,
    });
  }

  return { lines, mat, hst, total };
};

export const getBookingBreakdown = (trip?: any) =>
  trip ? calcBookingBreakdown(trip) : null;

